import {
  ActionIcon,
  Anchor,
  Box,
  Container,
  Grid,
  Group,
  Menu,
  Stack,
  Text,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import {
  IconBrandFacebook,
  IconBrandLinkedin,
  IconBrandX,
  IconLink,
  IconShare,
} from "@tabler/icons-react";
import { Link } from "@inertiajs/react";
import { L2KH1 } from "../../components/ui/Typography";
import AuthenticatedLayout from "../../components/layouts/AuthenticatedLayout";
import { MarkdownRenderer } from "../../components/posts/MarkdownRenderer";
import { CaptivateEmbed } from "../../components/posts/CaptivateEmbed";
import { PostTypeBadges } from "../../components/posts/PostTypeBadges";
import { PostStats } from "../../components/posts/PostStats";
import { ContentActionsMenu } from "../../components/ui/ContentActionsMenu";
import { KeepReading } from "../../components/ui/KeepReading/KeepReading";
import { readingTime } from "../../lib/readingTime";
import type { PostDetail, PostSummary } from "../../types/post";
import { SEO } from "../../components/common/SEO";

interface Props {
  post: PostDetail;
  related: PostSummary[];
  view_count: number | null;
  is_fresh: boolean;
}

interface ShareBundle {
  shareUrl: string;
  shareText: string;
  shareLinks: { x: string; facebook: string; linkedin: string };
  onCopyLink: () => void;
}

// Single share affordance used on every viewport. Prefers navigator.share()
// (native OS share sheet — iOS/Android, plus desktop Chrome/Edge/Safari) so
// users get the full list of their installed apps and destinations. Falls
// back to a Mantine Menu popover with the same options for browsers that
// don't implement the Web Share API (Firefox desktop, older browsers).
function ShareButton({
  shareUrl,
  shareText,
  shareLinks,
  onCopyLink,
}: ShareBundle) {
  const canNativeShare =
    typeof navigator !== "undefined" &&
    typeof (navigator as Navigator).share === "function";

  const handleNativeShare = async () => {
    try {
      await navigator.share({ title: shareText, url: shareUrl });
    } catch (err) {
      // AbortError = user dismissed the sheet; silently ignore.
      if ((err as Error).name !== "AbortError") {
        notifications.show({
          message: "Sharing failed — long-press the URL bar to copy",
          color: "red",
          autoClose: 3000,
        });
      }
    }
  };

  if (canNativeShare) {
    return (
      <ActionIcon
        onClick={handleNativeShare}
        variant="subtle"
        color="gray"
        size="lg"
        aria-label="Share this post"
      >
        <IconShare size={18} />
      </ActionIcon>
    );
  }

  return (
    <Menu position="bottom-end" width={200} shadow="md">
      <Menu.Target>
        <ActionIcon
          variant="subtle"
          color="gray"
          size="lg"
          aria-label="Share this post"
        >
          <IconShare size={18} />
        </ActionIcon>
      </Menu.Target>
      <Menu.Dropdown>
        <Menu.Item
          component="a"
          href={shareLinks.x}
          target="_blank"
          rel="noopener noreferrer"
          leftSection={<IconBrandX size={16} />}
        >
          Share on X
        </Menu.Item>
        <Menu.Item
          component="a"
          href={shareLinks.facebook}
          target="_blank"
          rel="noopener noreferrer"
          leftSection={<IconBrandFacebook size={16} />}
        >
          Share on Facebook
        </Menu.Item>
        <Menu.Item
          component="a"
          href={shareLinks.linkedin}
          target="_blank"
          rel="noopener noreferrer"
          leftSection={<IconBrandLinkedin size={16} />}
        >
          Share on LinkedIn
        </Menu.Item>
        <Menu.Item onClick={onCopyLink} leftSection={<IconLink size={16} />}>
          Copy link
        </Menu.Item>
      </Menu.Dropdown>
    </Menu>
  );
}

export default function PostsShow({ post, related, view_count, is_fresh }: Props) {
  const formatted = post.published_at
    ? new Date(post.published_at).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      })
    : "";

  const description =
    post.summary ||
    post.body.slice(0, 160).replace(/[#*_`\[\]]/g, "").trim();

  const shareUrl = `https://learntokiz.io/posts/${post.slug}`;
  const shareText = post.title;
  const shareLinks = {
    x: `https://x.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareText)}`,
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`,
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`,
  };
  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      notifications.show({
        message: "Link copied to clipboard",
        color: "orange",
        autoClose: 2000,
      });
    } catch {
      notifications.show({
        message: "Couldn't copy — long-press the URL bar instead",
        color: "red",
        autoClose: 3000,
      });
    }
  };

  return (
    <AuthenticatedLayout>
      <SEO
        title={`${post.title} — Learntokiz Posts`}
        description={description}
        image={post.cover_image_url || "/images/og-image-blog.png"}
        url={`https://learntokiz.io/posts/${post.slug}`}
        type="article"
      />
      <Container size="lg" style={{ paddingTop: 12, paddingBottom: 48 }}>
        {/* Inline-flow breadcrumb: the title wraps naturally after "Posts /"
            on the same line, then to a new line when it runs out of room.
            Mantine <Breadcrumbs> locks white-space: nowrap on its slot,
            which is why we roll a plain block here. */}
        <Box mb="md" style={{ lineHeight: 1.5 }}>
          <Anchor
            component={Link}
            href="/posts"
            size="sm"
            c="dimmed"
            className="blog-breadcrumb-link"
            style={{ whiteSpace: "nowrap" }}
          >
            Posts
          </Anchor>
          <Text
            size="sm"
            c="dimmed"
            span
            aria-hidden="true"
            style={{ margin: "0 8px" }}
          >
            /
          </Text>
          <Text
            size="sm"
            c="dimmed"
            span
            style={{ wordBreak: "break-word" }}
          >
            {post.title}
          </Text>
        </Box>

        <Grid gap={{ base: "md", lg: "xl" }}>
          <Grid.Col span={{ base: 12, lg: 8 }}>
            <Box maw="72ch" className="blog-article-enter">
              {/* Order: cover → title → single meta bar. The badge lives
                  in the meta row (same visual weight as date/reading time)
                  so it reads as post metadata, not identity scaffolding. */}
              {post.cover_image_url && (
                <Box
                  mb="lg"
                  style={{
                    borderRadius: 12,
                    overflow: "hidden",
                    aspectRatio: "16 / 9",
                    background: "var(--surface-placeholder)",
                  }}
                >
                  <img
                    src={post.cover_image_url}
                    alt={post.title}
                    width={1600}
                    height={900}
                    fetchPriority="high"
                    decoding="async"
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      display: "block",
                    }}
                  />
                </Box>
              )}

              <Stack gap="xs" mb="xl">
                <L2KH1 style={{ textWrap: "balance" }}>{post.title}</L2KH1>
                <Group gap="md" wrap="wrap" align="center">
                  <PostTypeBadges
                    postType={post.post_type}
                    podcastNumber={post.podcast_number}
                    size="sm"
                  />
                  <Text size="sm" c="dimmed">
                    {formatted} · {readingTime(post.body)} min read
                  </Text>
                  <PostStats viewCount={view_count} isFresh={is_fresh} />

                  {/* Single Share button on all viewports — native OS share
                      sheet where supported, popover fallback elsewhere. */}
                  <Box ml="auto">
                    <ShareButton
                      shareUrl={shareUrl}
                      shareText={shareText}
                      shareLinks={shareLinks}
                      onCopyLink={handleCopyLink}
                    />
                  </Box>

                  {/* Admin-only kebab (Edit shortcut). Renders nothing for
                      non-admins now that Copy Link lives in the Share menu. */}
                  <ContentActionsMenu
                    editUrl={`/admin/posts/${post.slug}/edit`}
                    ariaLabel="More post actions"
                    showCopy={false}
                  />
                </Group>
              </Stack>

              {post.captivate_show_id && (
                <Box mb="lg">
                  <CaptivateEmbed showId={post.captivate_show_id} />
                </Box>
              )}

              <MarkdownRenderer source={post.body} />
            </Box>
          </Grid.Col>

          <Grid.Col span={{ base: 12, lg: 4 }}>
            <KeepReading related={related} />
          </Grid.Col>
        </Grid>
      </Container>
    </AuthenticatedLayout>
  );
}
